// IncludesTab.tsx
import React from "react";
import {ProcessedTripItinerary, TourOperator} from "../contentfulModel";
import {renderRichText, RichTextNode} from "../utils.tsx";

interface IncludesTabProps {
    tourPackage: ProcessedTripItinerary;
    tourOperator: TourOperator
}

const getTickIcon = (color: string) => (
    <svg width="18" height="13" viewBox="0 0 18 13" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path
            d="M7.33702 12.6191C6.83476 13.127 6.0231 13.127 5.52084 12.6191L0.376575 7.41827C-0.125525 6.91038 -0.125525 6.08962 0.376575 5.58173C0.878754 5.07384 1.69282 5.07384 2.19508 5.58173L6.39277 9.86021L15.8071 0.380919C16.3094 -0.126973 17.121 -0.126973 17.6233 0.380919C18.1256 0.888811 18.1256 1.70956 17.6233 2.21746L7.33702 12.6191Z"
            fill={color}/>
    </svg>
)

export const IncludesTab: React.FC<IncludesTabProps> = ({ tourPackage, tourOperator }) => {
    return (
        <div className="flex flex-col md:flex-row w-full h-full overflow-auto">
            {/* Mobile: Content below image, Desktop: Content on left */}
            <div className="order-last md:order-first w-full md:w-[50%] flex flex-col ">
                <div id="includContainer"
                     className="w-full bg-[#F7F6F2] flex-grow p-4 md:p-8 text-left md:pt-16 md:px-[70px]">
                    {/* Tour Package Information */}
                    <div className="mb-4">
                        <div className="text-md uppercase md:text-xl md:font-medium mb-1 md:pt-12">
                            {tourPackage.category}
                        </div>
                        <h2 className="text-xl md:text-2xl font-semibold md:font-black mb-1">
                            {tourPackage.name}
                        </h2>
                        {tourPackage.subtitle && (
                            <p className="text-lg font-normal md:font-semibold">
                                {tourPackage.subtitle}
                            </p>
                        )}
                    </div>

                    <div className="mt-6 bg-white p-4 rounded-2xl mb-16">
                        <h3 className="text-md md:text-lg font-bold mb-4">Qué incluye:</h3>
                        {tourPackage.termsAndPricing?.includes && (
                            <ul className="text-sm md:text-md space-y-2">
                                {tourPackage.termsAndPricing.includes.map((item, index) => (
                                    <li key={index} className="flex items-start gap-3 mb-3">
                                        <div className="flex-shrink-0 mt-0.5">
                                            {getTickIcon(tourOperator.primaryColor)}
                                        </div>
                                        <div className="flex-grow">{item}</div>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                </div>
            </div>

            {/* Mobile: Image on top, Desktop: Image on right */}
            <div className="order-first md:order-last w-full md:w-[50%] h-[200px] md:h-full">
                <div className="h-full relative">
                    <div className="min-h-[270px] max-h-[270px] h-full rounded-b-3xl">
                    <img
                        src={tourPackage.termsAndPricing?.includesImage?.file ||
                            tourPackage.mainImage?.file || ''}
                        alt="Qué incluye"
                        style={{
                            objectPosition: "center"
                        }}
                        className="w-full h-full object-cover rounded-b-2xl md:rounded-r-none md:rounded-l-2xl"
                    />
                    </div>
                    <div>
                        <div className="mt-6 bg-white p-4 rounded-2xl mb-16 px-16">
                            <h3 className="text-xl font-semibold mb-4 text-start" >
                                Observaciones
                            </h3>
                            <div className="text-start text-sm md:text-md">
                                {tourPackage.termsAndPricing.observations &&
                                    renderRichText(
                                        getTickIcon(tourOperator.primaryColor),
                                        tourPackage.termsAndPricing.observations as unknown as RichTextNode
                                    )
                                }
                            </div>
                        </div>
                    </div>
                    <img
                        src={tourOperator.logoContrast?.file || ''}
                        className="w-40 absolute hidden md:block bottom-4 right-4"
                        alt={tourOperator.name}
                    />
                </div>
            </div>
        </div>
    );
};
